/* HubSpot vault — private app token + sync-instellingen. */
import GenericVault from "./GenericVault";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, Loader2, RefreshCcwDot, Building2, Users, Handshake } from "lucide-react";

/* ----------------------------------------------------------------- */
/*  Generic vault — api_key only (Apollo/Surfe/Fireflies/TheOrg/Perplexity)   */
/* ----------------------------------------------------------------- */
/* ----------------------------------------------------------------- */
/*  HubSpotVault — Private App token + sync-paneel (companies,        */
/*  contacten & deals pushen naar de HubSpot van deze workspace).     */
/* ----------------------------------------------------------------- */
function HubSpotVault({ provider, cfg, onSaved, appBaseUrl }) {
    const { t } = useTranslation();
    const [syncing, setSyncing] = useState(false);
    const [status, setStatus] = useState(null);

    const loadStatus = async () => {
        try { const r = await api.get("/hubspot/status"); setStatus(r.data); }
        catch { /* stil — paneel toont dan geen historiek */ }
    };
    useEffect(() => { if (cfg.configured) loadStatus(); }, [cfg.configured]);

    const runSync = async () => {
        if (!window.confirm(t("integr.hubspot_sync_confirm"))) return;
        setSyncing(true);
        try {
            const { data } = await api.post("/hubspot/sync");
            const s = data.stats;
            toast.success(t("integr.hubspot_sync_done", {
                companies: s.companies.created + s.companies.updated,
                contacts: s.contacts.upserted,
                deals: s.deals.created + s.deals.updated,
            }), { duration: 8000 });
            if ((data.errors || []).length > 0) {
                toast.error(`${data.errors.length} fout(en): ${data.errors[0]}`, { duration: 8000 });
            }
            loadStatus();
        } catch (e) { toast.error(e?.response?.data?.detail || t("integr.hubspot_sync_failed")); }
        finally { setSyncing(false); }
    };

    const last = status?.last_sync;
    return (
        <div className="space-y-4">
            <GenericVault provider={provider} cfg={cfg} onSaved={onSaved} appBaseUrl={appBaseUrl} />
            {cfg.configured && (
                <div className="rounded-xl border border-[#ff7a59]/25 bg-[#ff7a59]/[0.04] p-4" data-testid="hubspot-sync-panel">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div>
                            <div className="text-sm font-medium text-white flex items-center gap-2">
                                <RefreshCcwDot size={14} className="text-[#ff7a59]" />
                                {t("integr.hubspot_sync_title")}
                            </div>
                            <p className="text-[11px] text-[var(--jr-text-3)] mt-1">{t("integr.hubspot_sync_desc")}</p>
                        </div>
                        <button onClick={runSync} disabled={syncing} data-testid="hubspot-sync-btn"
                            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium bg-[#ff7a59] text-black hover:bg-[#ff7a59]/85 disabled:opacity-50">
                            {syncing ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                            {syncing ? t("integr.hubspot_syncing") : t("integr.hubspot_sync_now")}
                        </button>
                    </div>
                    {last && (
                        <div className="mt-3 pt-3 border-t border-white/8 grid grid-cols-3 gap-3 text-center" data-testid="hubspot-sync-stats">
                            {[
                                { icon: Building2, label: t("integr.hubspot_companies"), v: last.stats.companies.created + last.stats.companies.updated },
                                { icon: Users, label: t("integr.hubspot_contacts"), v: last.stats.contacts.upserted },
                                { icon: Handshake, label: t("integr.hubspot_deals"), v: last.stats.deals.created + last.stats.deals.updated },
                            ].map((x, i) => {
                                const XI = x.icon;
                                return (
                                    <div key={i}>
                                        <div className="text-lg font-display text-white">{x.v}</div>
                                        <div className="text-[10px] font-mono uppercase tracking-[0.14em] text-[var(--jr-text-3)] inline-flex items-center gap-1">
                                            <XI size={10} /> {x.label}
                                        </div>
                                    </div>
                                );
                            })}
                            <div className="col-span-3 text-[10px] text-[var(--jr-text-3)]">
                                {t("integr.hubspot_last_sync")}: {new Date(last.synced_at).toLocaleString()}
                                {(last.errors || []).length > 0 && (
                                    <span className="text-[#ef4444] ml-2">· {last.errors.length} fout(en)</span>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default HubSpotVault;
